/**
 * Página de redirección a Redsys
 * 
 * Esta página maneja la redirección automática a la pasarela de pago de Redsys.
 * Carga los datos de la transacción y envía el formulario automáticamente.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import paymentService from '../services/paymentService';
import { toast } from 'react-toastify';
import '../styles/payment-result.css';

const RedsysRedirect = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redsysForm, setRedsysForm] = useState(null);

  useEffect(() => {
    const loadTransactionAndRedirect = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!transactionId) {
          throw new Error('ID de transacción no proporcionado');
        }

        if (!user) {
          throw new Error('Usuario no autenticado');
        }

        // Obtener detalles de la transacción
        const response = await paymentService.getTransactionById(transactionId);
        
        if (!response.success) {
          throw new Error(response.message || 'Error al cargar la transacción');
        }

        const transaction = response.data;

        // Verificar que la transacción pertenece al usuario actual
        const transactionUserId = transaction.user?._id || transaction.user;
        if (transactionUserId.toString() !== user._id.toString()) {
          throw new Error('No tienes permisos para acceder a esta transacción');
        }

        // Verificar que la transacción está en estado pendiente
        if (transaction.status !== 'pending') {
          throw new Error('Esta transacción ya ha sido procesada');
        }

        // Si la transacción tiene datos de Redsys, preparar redirección
        if (transaction.redsysData && transaction.redsysData.signature) {
          // Reconstruir el formulario de Redsys
          const formData = {
            Ds_SignatureVersion: 'HMAC_SHA256_V1',
            Ds_MerchantParameters: transaction.redsysData.merchantParameters,
            Ds_Signature: transaction.redsysData.signature,
            redsysUrl: transaction.redsysData.environment === 'production' 
              ? 'https://sis.redsys.es/sis/realizarPago'
              : 'https://sis-t.redsys.es:25443/sis/realizarPago'
          };

          setRedsysForm(formData);

          // Redirigir automáticamente después de un breve delay
          setTimeout(() => {
            redirectToRedsys(formData);
          }, 2000);
        } else {
          throw new Error('Datos de Redsys no encontrados en la transacción');
        }

      } catch (error) {
        console.error('Error al cargar transacción:', error);
        setError(error.message || 'Error al procesar la transacción');
        toast.error(error.message || 'Error al procesar la transacción');
      } finally {
        setLoading(false);
      }
    };

    loadTransactionAndRedirect();
  }, [transactionId, user, navigate]);

  /**
   * Redirigir a Redsys con el formulario
   */
  const redirectToRedsys = (formData) => {
    try {
      if (!formData || !formData.redsysUrl) {
        throw new Error('Datos de Redsys incompletos');
      }

      // Crear formulario dinámico para enviar a Redsys
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = formData.redsysUrl;
      form.style.display = 'none';

      // Añadir campos ocultos
      const fields = {
        'Ds_SignatureVersion': formData.Ds_SignatureVersion,
        'Ds_MerchantParameters': formData.Ds_MerchantParameters,
        'Ds_Signature': formData.Ds_Signature
      };

      Object.keys(fields).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = fields[key];
        form.appendChild(input);
      });

      // Añadir al DOM y enviar
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Error al redirigir a Redsys:', error);
      setError('Error al redirigir a la pasarela de pago');
      toast.error('Error al redirigir a la pasarela de pago');
    }
  };

  /**
   * Reintentar redirección manual
   */
  const handleRetry = () => {
    if (redsysForm) {
      redirectToRedsys(redsysForm);
    }
  };

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card">
            <div className="loading">
              <div className="spinner"></div>
              <h2>Preparando pago...</h2>
              <p>Te estamos redirigiendo a la pasarela de pago segura.</p>
              <p>Por favor, no cierres esta ventana.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-page">
        <div className="container">
          <div className="payment-result-card error">
            <div className="result-icon">
              <span className="material-icons">error</span>
            </div>
            <h2>Error al procesar el pago</h2>
            <p>{error}</p>
            <div className="result-actions">
              {redsysForm && (
                <button onClick={handleRetry} className="btn btn-primary">
                  <span className="material-icons">refresh</span>
                  Reintentar
                </button>
              )}
              <button onClick={() => navigate('/wallet')} className="btn btn-secondary">
                Volver al Monedero
              </button>
              <button onClick={() => navigate('/')} className="btn btn-secondary">
                Ir al Inicio
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className="container">
        <div className="payment-result-card">
          <div className="payment-confirmation">
            <div className="result-icon">
              <span className="material-icons">credit_card</span>
            </div>
            <h2>Confirmar Pago</h2>
            <p>Serás redirigido a la pasarela de pago segura de Redsys para completar la transacción.</p>
            <div className="payment-details">
              <p><strong>¿Deseas proceder con el pago?</strong></p>
            </div>
            <div className="result-actions">
              <button onClick={handleRetry} className="btn btn-primary">
                <span className="material-icons">credit_card</span>
                Proceder al Pago
              </button>
              <button onClick={() => navigate('/products')} className="btn btn-secondary">
                <span className="material-icons">arrow_back</span>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedsysRedirect;
